import React, { useEffect, useState } from 'react'
import classes from './BrowseCourses.module.css'
import { AuthenticatedHeader } from '../../../components/Header/AuthenticatedHeader'
import Footer from '../../../components/Footer/Footer'
import gridClasses from '../../../styles/grid.module.css'
import classNames from 'classnames'
import ToggleBar from '../../../components/ToggleBar/ToggleBar'
import { CourseCard } from '../../../components/Cards/GeneralCard/Card'
import PageChanger from '../../../components/PageChanger/PageChanger'
import { Button, ButtonIcon } from '../../../components/Button/Button'
import { ReactComponent as HeartIcon } from '../../../assets/icons/heart.svg'
import { ReactComponent as HeartFilledIcon } from '../../../assets/images/heart-filled.svg'
import { ReactComponent as RightIcon } from '../../../assets/icons/arrow-right.svg'
import TooltipWrapper from '../../../components/Tooltip/Tooltip'
import { useDispatch, useSelector } from 'react-redux'
import { browseCoursesSelectors } from './browseCoursesSlice'
import { getCourses } from './browseCoursesApi'
import CardLoader from '../../../components/loaders/CardLoader'
import notification from '../../../components/Notification/notification'
import { categoriesSelectors } from '../categoriesSlice'
import { getCategories } from '../categoriesApi'
import { userSelectors } from '../../../redux/userSlice'
import { addCourseToCart } from '../../Cart/cartApi'
import { getFreeCoursesLeft, getImageURL } from '../../../util/functions'
import placeholder from '../../../assets/images/course-placeholder.svg'
import empty from '../../../assets/images/empty-course.svg'
import Empty from '../../../components/Empty/Empty'
import { useAuthReminderModal } from '../../../hooks/useAuthReminderModal'
import {
    isItemInWishlist,
    removeFromWishlist,
    addToWishlist,
} from '../../WishList/wishListApi'
import { wishListSelectors } from '../../WishList/wishListSlice'
import { cartSelectors } from '../../Cart/cartSlice'
import { AccountTypes, CourseTypes } from '../../../util/constants'
import { useHistory } from 'react-router-dom'

const BrowseCourses = () => {
    const dispatch = useDispatch()
    const history = useHistory()

    //Categories Logic
    const [activeCategory, setActiveCategory] = useState(null)
    const categories = useSelector(categoriesSelectors.categories)
    const categoriesHasError = useSelector(categoriesSelectors.hasError)
    const categoriesErrorMessage = useSelector(categoriesSelectors.errorMessage)
    const categoriesIsFetching = useSelector(categoriesSelectors.isFetching)

    useEffect(() => {
        if (!categoriesIsFetching && categoriesHasError) {
            notification.failure({
                title: 'Something went wrong with fetching categories',
                description: categoriesErrorMessage,
            })
        }
    }, [categoriesIsFetching])

    //Courses logic
    const fetchCourses = ({ pageNo = 1, pageSize = 10 }) => {
        dispatch(getCourses({ pageNo, pageSize, catId: activeCategory }))
    }

    const [page, setPage] = useState(1)

    useEffect(() => {
        fetchCourses({ pageNo: page })
    }, [page, activeCategory])

    useEffect(() => {
        dispatch(getCategories('courses'))
    }, [])

    const courses = useSelector(browseCoursesSelectors.courses)
    const isFetching = useSelector(browseCoursesSelectors.isFetching)
    const hasError = useSelector(browseCoursesSelectors.hasError)
    const errorMessage = useSelector(browseCoursesSelectors.errorMessage)
    const paging = useSelector(browseCoursesSelectors.paging)

    const wishlistCourses = useSelector(wishListSelectors.courses)

    useEffect(() => {
        if (!isFetching && hasError) {
            notification.failure({
                title: 'Something went wrong',
                description: errorMessage,
            })
        }
    }, [isFetching])

    const isLoggedIn = useSelector(userSelectors.isLoggedIn)

    const [addingToCart, setAddingToCart] = useState(false)

    const addToCart = (course, free) => {
        if (isLoggedIn) {
            notification.loading({
                title: 'Adding to cart',
            })
        }
        setAddingToCart(true)
        dispatch(
            addCourseToCart({
                id: course.coursecode,
                course,
                free,
            })
        )
            .then(() => {
                notification.destroy()
                notification.success({
                    title: 'Success!',
                    description: `${course.title} successfully added to cart`,
                })
                setAddingToCart(false)
            })
            .catch(error => {
                // console.log("gad", error.response, error.message)
                notification.destroy()
                if (error?.response?.status === 400) {
                    notification.info({
                        title: error?.response?.data,
                    })
                } else {
                    notification.info({
                        title: 'Failed to add to cart',
                        description: error?.message,
                    })
                }
                setAddingToCart(false)
            })
    }

    const { renderModal, conditionallyShowModal } = useAuthReminderModal()

    const handleAddToWishlist = course => event => {
        event.preventDefault()
        conditionallyShowModal(() => {
            if (isItemInWishlist(wishlistCourses, course.coursecode)) {
                dispatch(
                    removeFromWishlist({
                        type: 'course',
                        itemId: course.coursecode,
                    })
                )
            } else {
                dispatch(
                    addToWishlist({
                        type: 'course',
                        itemId: course.coursecode,
                    })
                )
            }
        })()
    }

    return (
        <>
            <AuthenticatedHeader />
            <div className={classes.top}>
                <div
                    className={classNames(
                        gridClasses.container,
                        gridClasses.containerTransparent
                    )}
                >
                    <ToggleBar
                        links={categories}
                        activeId={activeCategory}
                        changeActive={setActiveCategory}
                        loading={categoriesIsFetching}
                    />
                </div>
            </div>
            <main className={gridClasses.container}>
                <h3 className={classes.coursesHeader}>
                    {categories.find(({ id }) => id === activeCategory)?.text}
                </h3>
                <div className={classes.coursesGrid}>
                    {isFetching ? (
                        <>
                            <CardLoader />
                            <CardLoader />
                            <CardLoader />
                        </>
                    ) : (
                        courses.map(course => (
                            <TooltipWrapper
                                key={course.coursecode}
                                tooltip={
                                    <CardTooltip
                                        {...course}
                                        students={course.enrolled}
                                        addToCart={free => {
                                            // addToCart(course, free)
                                            history.push(
                                                `/app/browse/courses/${course.coursecode}`
                                            )
                                        }}
                                        addingToCart={addingToCart}
                                        addToWishlist={handleAddToWishlist(
                                            course
                                        )}
                                        itemInWishlist={isItemInWishlist(
                                            wishlistCourses,
                                            course.coursecode
                                        )}
                                        addToCartCustomText="LEARN MORE"
                                        hasEnrolled={course.isEnrolled}
                                    />
                                }
                            >
                                <CourseCard
                                    {...course}
                                    url={
                                        course.thumbnail
                                            ? getImageURL(course.thumbnail)
                                            : placeholder
                                    }
                                    attendees={course.enrolled}
                                    className={classes.course}
                                    premium={
                                        course.courseType ===
                                        CourseTypes.Premium
                                    }
                                    showStudentCount={
                                        `${course.showstudentcount}` === '1'
                                    }
                                />
                            </TooltipWrapper>
                        ))
                    )}
                </div>
                {!isFetching && courses.length === 0 && (
                    <Empty text={'No Courses Found'} icon={empty} />
                )}
                {!isFetching && courses.length > 0 && (
                    <div className={classes.pageChangerWrapper}>
                        <PageChanger
                            pageCount={paging.pageCount}
                            onPageChange={setPage}
                            currentPage={page}
                        />
                    </div>
                )}
                {renderModal()}
            </main>
            <Footer />
        </>
    )
}

export const CardTooltip = ({
    title,
    category,
    students,
    lectures,
    downloads,
    description,
    className = '',
    addToCart,
    addingToCart,
    addToWishlist,
    itemInWishlist,
    courseType,
    hasEnrolled,
    addToCartCustomText,
}) => {
    const items = useSelector(cartSelectors.cartItems)
    const user = useSelector(userSelectors.userDetails)
    const subscribedUser =
        String(user.accounttype).toLowerCase() ===
        AccountTypes.Subscriber?.toLowerCase()
    const userFreeCourses = getFreeCoursesLeft({
        items,
        freeCourses: user.freeCourses,
    })
    let hasFree = false
    if (
        subscribedUser &&
        userFreeCourses > 0 &&
        courseType === CourseTypes.Free_For_Subscribers
    ) {
        hasFree = true
    }

    return (
        <div className={classNames(classes.cardTooltip, className)}>
            <h4 className={classes.cardTooltipTitle}>{title}</h4>
            <p className={classes.cardTooltipCategory}>{category}</p>
            <div className={classes.cardTooltipDetails}>
                <p className={classes.cardTooltipDetail}>
                    <span>{lectures} lectures</span>
                </p>
                <p className={classes.cardTooltipDetail}>
                    <span>{students} students</span>
                </p>
                <p className={classes.cardTooltipDetail}>
                    <span>{downloads} downloads</span>
                </p>
            </div>
            <p className={classes.cardTooltipDescription}>{description}</p>
            <div className={classes.cardTooltipButtons}>
                <Button
                    bold={false}
                    onClick={() => !hasEnrolled && addToCart(hasFree)}
                    disabled={addingToCart}
                    style={{
                        fontSize: 14,
                        cursor: hasEnrolled ? 'not-allowed' : 'pointer',
                    }}
                >
                    {addToCartCustomText ||
                        (hasEnrolled
                            ? 'Enrolled'
                            : hasFree
                            ? 'Get For Free'
                            : 'Add to Cart')}
                    {!hasEnrolled && <ButtonIcon icon={<RightIcon />} />}
                </Button>
                {!hasEnrolled && (
                    <Button
                        iconOnly={'true'}
                        label={'Add to wishlist'}
                        onClick={addToWishlist}
                    >
                        <ButtonIcon
                            icon={
                                itemInWishlist ? (
                                    <HeartFilledIcon />
                                ) : (
                                    <HeartIcon />
                                )
                            }
                        />
                    </Button>
                )}
            </div>
        </div>
    )
}

export default BrowseCourses
